import React from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { UserPlus, Sparkles, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useSocial } from "@/context/SocialContext";

/**
 * Global toast-style banner for social WebSocket events. All state (queue,
 * dismissal, WS listener) lives in SocialProvider — this component is now
 * a pure renderer, keeping the visual layer decoupled from transport.
 */
export default function FriendNotifier() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toasts, dismissToast } = useSocial();

  if (!user || toasts.length === 0) return null;

  const handleAction = (t) => {
    if (t.target) navigate(t.target);
    dismissToast(t.id);
  };

  return (
    <div
      className="fixed z-[2147480000] pointer-events-none top-3 right-3 md:top-5 md:right-6 flex flex-col gap-2 max-w-[min(92vw,360px)]"
      data-testid="friend-notifier"
      aria-live="polite"
    >
      <AnimatePresence initial={false}>
        {toasts.map((t) => (
          <motion.div
            key={t.id}
            role="button"
            tabIndex={0}
            initial={{ opacity: 0, x: 32, scale: 0.96 }}
            animate={{ opacity: 1, x: 0, scale: 1 }}
            exit={{ opacity: 0, x: 32, scale: 0.96 }}
            transition={{ type: "spring", stiffness: 300, damping: 24 }}
            onClick={() => handleAction(t)}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") handleAction(t); }}
            data-testid={`friend-toast-${t.type}`}
            className={`pointer-events-auto cursor-pointer text-left w-full flex items-center gap-3 border-hard shadow-hard rounded-2xl p-3 press-btn ${t.type === "accepted" ? "bg-[#CCFF00]" : "bg-white"}`}
          >
            <div
              className="w-11 h-11 shrink-0 rounded-xl border-hard-2 flex items-center justify-center"
              style={{ background: t.type === "accepted" ? "#FFFFFF" : "#D4B3FF" }}
            >
              {t.type === "accepted"
                ? <Sparkles strokeWidth={3} className="w-5 h-5" />
                : <UserPlus strokeWidth={3} className="w-5 h-5" />}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-black text-sm leading-tight">{t.title}</div>
              <div className="text-[12px] text-[#333] leading-tight truncate">{t.subtitle}</div>
              <div className="text-[10px] uppercase font-black tracking-widest mt-0.5 text-[#0A0A0A]">{t.actionLabel} →</div>
            </div>
            <span
              onClick={(e) => { e.stopPropagation(); dismissToast(t.id); }}
              onMouseDown={(e) => e.stopPropagation()}
              className="w-8 h-8 flex items-center justify-center border-hard-2 rounded-lg bg-white shrink-0 cursor-pointer"
              role="button"
              aria-label="Kapat"
              data-testid={`friend-toast-close-${t.id}`}
            >
              <X strokeWidth={3} className="w-3.5 h-3.5" />
            </span>
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  );
}
